import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://app.solteros.it/speeddate/api';

// Cost constants (mirrors backend)
const FIXED_MONTHLY = 48;
const FIRST_TIER = 500;
const FIRST_TIER_PRICE = 0.50;
const SECOND_TIER_PRICE = 1.00;

function getQuarterInfo(date) {
  const m = date.getMonth();
  const y = date.getFullYear();
  if (m < 3) return { label: `Q1 ${y}`, start: new Date(y, 0, 1), end: new Date(y, 2, 31, 23, 59, 59) };
  if (m < 6) return { label: `Q2 ${y}`, start: new Date(y, 3, 1), end: new Date(y, 5, 30, 23, 59, 59) };
  if (m < 9) return { label: `Q3 ${y}`, start: new Date(y, 6, 1), end: new Date(y, 8, 30, 23, 59, 59) };
  return { label: `Q4 ${y}`, start: new Date(y, 9, 1), end: new Date(y, 11, 31, 23, 59, 59) };
}

// Data fattura: per default il giorno 5 del mese successivo alla fine del quarter
// (es. quarter ott-dic 2026 → 5 Gennaio 2027)
function getInvoiceDate(q) {
  const end = new Date(q.end);
  return new Date(end.getFullYear(), end.getMonth() + 1, 5);
}

function computeQuarterCosts(ticketCount) {
  const fixed = FIXED_MONTHLY * 3;
  const tier1Count = Math.min(ticketCount, FIRST_TIER);
  const tier2Count = Math.max(0, ticketCount - FIRST_TIER);
  const tier1Revenue = parseFloat((tier1Count * FIRST_TIER_PRICE).toFixed(2));
  const tier2Revenue = parseFloat((tier2Count * SECOND_TIER_PRICE).toFixed(2));
  const variable = tier1Revenue + tier2Revenue;
  const total = fixed + variable;
  return {
    fixedCost: fixed,
    tier1Count,
    tier2Count,
    tier1Revenue,
    tier2Revenue,
    variableCost: variable,
    totalCost: parseFloat(total.toFixed(2))
  };
}

const statusLabels = {
  0: { label: 'Creato', color: 'secondary' },
  1: { label: 'Attivo', color: 'success' },
  2: { label: 'Pausa', color: 'warning' },
  3: { label: 'Iniziato', color: 'info' },
  4: { label: 'Gioco', color: 'primary' },
  5: { label: 'Terminato', color: 'dark' },
  6: { label: 'Risultati Pubbl.', color: 'success' },
  7: { label: 'Inattivo', color: 'light text-muted' },
  8: { label: 'Cancellato', color: 'danger' }
};

// SOLA-51 (reopened): il conteggio dei partecipanti da fatturare (billed=false)
// NON usa più la data dell'evento: tutti gli unbilled di eventi completati
// (stato > 5) confluiscono nel trimestre della PROSSIMA fattura (il primo
// trimestre con data fattura non ancora emessa). I fatturati (billed=1)
// restano nel trimestre dell'evento.

export default function BillingPage() {
  const [quarters, setQuarters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedQuarter, setExpandedQuarter] = useState(null);

  const exportQuarterJson = (q) => {
    const qLabel = q.label.toLowerCase().replace('q', '').split(' ');
    const qNum = qLabel[0];
    const year = qLabel[1];
    const quarterName = `${qNum}° trimestre ${year}`;

    const lines = [
      {
        DESCRIZIONE: `Canone manutenzione app Solteros ${quarterName}`,
        SIGLA: 'MAN',
        QUANTITA: 3,
        IMPORTO_UNITARIO: 48
      },
      {
        DESCRIZIONE: `Quota variabile Primo Tier manutenzione app Solteros ${quarterName} da contratto 23/6/26`,
        SIGLA: 'VAR',
        QUANTITA: q.tier1Count,
        IMPORTO_UNITARIO: 0.5
      }
    ];

    if (q.tier2Count > 0) {
      lines.push({
        DESCRIZIONE: `Quota variabile Secondo Tier manutenzione app Solteros ${quarterName} da contratto 23/6/26`,
        SIGLA: 'VAR',
        QUANTITA: q.tier2Count,
        IMPORTO_UNITARIO: 1
      });
    }

    const blob = new Blob([JSON.stringify(lines, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  useEffect(() => {
    setLoading(true);
    setError(null);

    const token = localStorage.getItem('adminToken');

    Promise.all([
      // SOLA-53: modalità leggera (senza coverImage e con partecipanti essenziali)
      // per la view billing — la risposta completa pesava ~23MB.
      axios.get(`${API_URL}/events?lite=1`, {
        headers: { 'x-admin-token': token }
      }),
      // SOLA-51: costi extra (tabella ExtraCosts) per il prospetto, raggruppati per Quarter.
      axios.get(`${API_URL}/extracosts`, {
        headers: { 'x-admin-token': token }
      })
    ])
      .then(([eventsRes, extrasRes]) => {
        // Costi extra: data/descrizione/costo/prezzo (prezzo può essere null)
        const extras = (extrasRes.data || []).map(ec => ({
          data: ec.data,
          descrizione: ec.descrizione || '',
          costo: parseFloat(ec.costo) || 0,
          // SOLA-51 (followup): il valore da esporre è `prezzo` (prezzo vendita = costo × 1.8);
          // se assente si calcola dal costo come fa il backfill di migrate.js.
          prezzo: ec.prezzo != null
            ? parseFloat(ec.prezzo)
            : parseFloat(((parseFloat(ec.costo) || 0) * 1.8).toFixed(2))
        }));

        // Eventi completati (stato > 5) di QUALSIASI data: nessun filtro temporale
        // (senza filtro sulla data la logica si semplifica, come proposto dall'utente).
        const events = (eventsRes.data || [])
          .filter(ev => ev.date && ev.status > 5)
          .map(ev => ({ ...ev, _d: new Date(ev.date) }));

        const quarterMap = {};

        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);

        // SOLA-51 (reopened): roll-forward. Un partecipante unbilled resta nel
        // trimestre dell'evento finché non è scattato il MESE successivo alla fine
        // del trimestre (1° giorno del mese dopo); poi confluisce nel trimestre
        // successivo, e così via. Il trigger è il mese, NON l'emissione fattura.
        const rollQuarter = (q) => {
          let cur = q;
          while (todayStart >= new Date(cur.end.getFullYear(), cur.end.getMonth() + 1, 1)) {
            cur = getQuarterInfo(new Date(cur.start.getFullYear(), cur.start.getMonth() + 3, 1));
          }
          return cur;
        };

        const ensureBucket = (q) => {
          if (!quarterMap[q.label]) {
            quarterMap[q.label] = {
              label: q.label,
              start: q.start,
              end: q.end,
              events: [],
              billedCount: 0,
              unbilledCount: 0,
              // SOLA-51: costi extra del trimestre — valore esposto: prezzo vendita
              extraPriceTotal: 0,
              extracosts: []
            };
          }
          return quarterMap[q.label];
        };

        events.forEach(ev => {
          const all = ev.participants || [];
          const billed = all.filter(p => p.billed);
          const unbilled = all.filter(p => !p.billed);

          const base = {
            id: ev.id,
            name: ev.name,
            date: ev.date,
            location: ev.location,
            city: ev.city,
            status: ev.status,
            participantCount: all.length
          };

          const evQ = getQuarterInfo(ev._d);
          const destQ = unbilled.length > 0 ? rollQuarter(evQ) : evQ;
          const sameQ = evQ.label === destQ.label;

          // I FATTURATI restano nel trimestre dell'evento (e nel dettaglio relativo).
          if (billed.length > 0 && !sameQ) {
            const b = ensureBucket(evQ);
            b.billedCount += billed.length;
            b.events.push({ ...base, billedCount: billed.length, unbilledCount: 0 });
          }

          // I DA FATTURARE vanno nel trimestre di destinazione (roll-forward).
          // Se coincide con quello dell'evento, tutto resta insieme.
          if (unbilled.length > 0 || (billed.length > 0 && sameQ)) {
            const b = ensureBucket(destQ);
            b.unbilledCount += unbilled.length;
            if (sameQ) b.billedCount += billed.length;
            b.events.push({
              ...base,
              billedCount: sameQ ? billed.length : 0,
              unbilledCount: unbilled.length
            });
          }
        });

        // SOLA-51: costi extra raggruppati per Quarter. Il quarter deve comparire
        // anche se ha SOLO costi extra. Il valore esposto è `prezzo` (× 1.8).
        extras.forEach(ec => {
          const q = getQuarterInfo(new Date(ec.data));
          const bucket = ensureBucket(q);
          bucket.extracosts.push(ec);
          bucket.extraPriceTotal = parseFloat((bucket.extraPriceTotal + ec.prezzo).toFixed(2));
        });

        // Sort quarters chronologically
        const sorted = Object.values(quarterMap).sort((a, b) => a.start - b.start);

        // Compute costs per quarter: variabile e totale suddivisi in
        // FATTURATI (billed) e DA FATTURARE (unbilled)
        const result = sorted.map(q => {
          const unb = computeQuarterCosts(q.unbilledCount);
          const bil = computeQuarterCosts(q.billedCount);
          const fixedCost = FIXED_MONTHLY * 3;
          // SOLA-51 (reopened): il canone fisso appartiene a UN SOLO gruppo per trimestre.
          // - se c'è almeno un partecipante fatturato (billed>0) → il canone è FATTURATO
          // - altrimenti → il canone è DA FATTURARE
          // Mai doppio conteggio: il totale dei due gruppi = canone (1 sola volta) + variabile.
          const hasBilled = q.billedCount > 0;
          const fixedBilled = hasBilled ? fixedCost : 0;
          const fixedUnbilled = hasBilled ? 0 : fixedCost;
          return {
            ...q,
            hasBilled,
            ticketCount: q.unbilledCount,
            fixedCost,
            // variabile: fatturati / da fatturare / totale
            varBilled: bil.variableCost,
            varUnbilled: unb.variableCost,
            varTotal: parseFloat((bil.variableCost + unb.variableCost).toFixed(2)),
            // totale per gruppo: il canone finisce in un solo gruppo (vedi regola sopra);
            // i costi extra (senza flag billed) finiscono sempre in DA FATTURARE
            totBilled: parseFloat((fixedBilled + bil.variableCost).toFixed(2)),
            totUnbilled: parseFloat((fixedUnbilled + unb.variableCost + q.extraPriceTotal).toFixed(2)),
            // totale trimestre complessivo: canone una sola volta + variabile + costi extra
            totalCost: parseFloat((fixedCost + unb.variableCost + bil.variableCost + q.extraPriceTotal).toFixed(2)),
            // tier per la quota DA FATTURARE (usati da export e badge)
            tier1Count: unb.tier1Count,
            tier2Count: unb.tier2Count,
            tier1Revenue: unb.tier1Revenue,
            tier2Revenue: unb.tier2Revenue,
            variableCost: unb.variableCost
          };
        });

        setQuarters(result);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="card border-0 shadow-sm glass">
        <div className="card-body text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Caricamento...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card border-0 shadow-sm glass">
        <div className="card-body text-center py-5">
          <i className="bi bi-exclamation-triangle text-danger fs-1"></i>
          <p className="text-danger mt-3">Errore nel caricamento dei dati di billing</p>
          <p className="text-muted small">{error}</p>
        </div>
      </div>
    );
  }

  const maxTickets = Math.max(...quarters.map(q => q.ticketCount), 1);

  return (
    <div className="container-fluid">
      <div className="row g-4">
        {/* Header */}
        <div className="col-12">
          <h4 className="fw-bold mb-0">
            <i className="bi bi-calculator"></i> Fatturazione Piattaforma
          </h4>
          
        </div>

        {/* Quarterly Table with expandable rows */}
        <div className="col-12">
          <div className="card border-0 shadow-sm glass">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="small text-uppercase fw-bold text-muted border-0 py-3 ps-4" style={{ width: '40px' }}></th>
                      <th className="small text-uppercase fw-bold text-muted border-0 py-3">Trimestre</th>
                      <th className="small text-uppercase fw-bold text-muted border-0 py-3">Periodo</th>
                      <th className="small text-uppercase fw-bold text-muted border-0 py-3 text-center">Data Fattura</th>
                      <th className="small text-uppercase fw-bold text-muted border-0 py-3 text-center">
                        Partecipanti<br />
                        <span className="text-warning" style={{ fontSize: '9px' }}>Da fatturare / Fatturati · Tier 1/2</span>
                      </th>
                      <th className="small text-uppercase fw-bold text-muted border-0 py-3 text-end">Costo Fisso</th>
                      <th className="small text-uppercase fw-bold text-muted border-0 py-3 text-end">
                        Costo Variabile<br />
                        <span className="text-warning" style={{ fontSize: '9px' }}>Da fatturare / Fatturati</span>
                      </th>
                      <th className="small text-uppercase fw-bold text-muted border-0 py-3 text-end">
                        Costi Extra<br />
                        <span className="text-warning" style={{ fontSize: '9px' }}>Costo extra</span>
                      </th>
                      <th className="small text-uppercase fw-bold text-muted border-0 py-3 text-end pe-4">
                        Totale<br />
                        <span className="text-warning" style={{ fontSize: '9px' }}>Da fatturare / Fatturati</span>
                      </th>
                      <th className="small text-uppercase fw-bold text-muted border-0 py-3 text-end pe-4">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quarters.map((q, i) => {
                      const isExpanded = expandedQuarter === i;
                      const invDate = getInvoiceDate(q);
                      const today = new Date();
                      const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                      const isInvoicePassed = todayStart >= invDate;
                      return (
                        <React.Fragment key={i}>
                          {/* Quarter Row */}
                          <tr
                            className={`cursor-pointer ${isExpanded ? 'table-active' : ''}`}
                            onClick={() => setExpandedQuarter(isExpanded ? null : i)}
                            style={{ cursor: 'pointer' }}
                          >
                            <td className="ps-4 py-3 text-center" style={{ width: '40px' }}>
                              <i className={`bi ${isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'} text-muted`}></i>
                            </td>
                            <td className="fw-bold py-3">{q.label}</td>
                            <td className="text-muted small py-3">
                              {new Date(q.start).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })} -
                              {' '}{new Date(q.end).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </td>
                            <td
                              className={`text-center text-nowrap py-3 ${isInvoicePassed ? 'bg-warning' : ''}`}
                              style={isInvoicePassed ? { fontWeight: '600' } : undefined}
                              title={isInvoicePassed
                                ? 'Data di fatturazione trascorsa'
                                : `Fattura prevista il ${invDate.toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' })}`}
                            >
                              {isInvoicePassed ? (
                                <span>
                                  <i className="bi bi-check-circle-fill me-1"></i>Fattura emessa
                                </span>
                              ) : (
                                invDate.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })
                              )}
                            </td>
                            <td className="text-center py-3">
                              <div className="d-flex flex-column align-items-center gap-1">
                                <span className="badge bg-primary rounded-pill" title="Da fatturare (Billed=0)">
                                  <i className="bi bi-x-circle me-1"></i>{q.unbilledCount} da fatturare
                                </span>
                                <span className="badge rounded-pill bg-success bg-opacity-10 text-success" style={{ fontSize: '11px' }} title="Già fatturati (Billed=1)">
                                  <i className="bi bi-check-circle me-1"></i>{q.billedCount} fatturati
                                </span>
                                <div className="d-flex gap-1">
                                  <span className="badge rounded-pill bg-warning bg-opacity-10 text-warning" style={{ fontSize: '10px' }}>
                                    €{q.tier1Revenue} ({q.tier1Count} × €0.50)
                                  </span>
                                  {q.tier2Count > 0 && (
                                    <span className="badge rounded-pill bg-danger bg-opacity-10 text-danger" style={{ fontSize: '10px' }}>
                                      €{q.tier2Revenue} ({q.tier2Count} × €1.00)
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="progress mt-2" style={{ height: '4px' }}>
                                <div
                                  className="progress-bar"
                                  style={{ width: `${(q.ticketCount / maxTickets) * 100}%` }}
                                ></div>
                              </div>
                            </td>
                            <td className="text-end text-info py-3">
                              <div title={q.hasBilled
                                ? `Canone €${q.fixedCost.toFixed(2)} incluso tra i Fatturati (il trimestre ha partecipanti fatturati)`
                                : `Canone €${q.fixedCost.toFixed(2)} da fatturare (nessun partecipante fatturato nel trimestre)`}>
                                €{q.fixedCost.toFixed(2)}
                              </div>
                              {q.hasBilled
                                ? <span className="badge rounded-pill bg-success bg-opacity-10 text-success" style={{ fontSize: '10px' }}>fatturato</span>
                                : <span className="badge rounded-pill bg-primary bg-opacity-10 text-primary" style={{ fontSize: '10px' }}>da fatturare</span>}
                            </td>
                            <td className="text-end text-warning py-3">
                              <div title="Costo variabile dei partecipanti da fatturare">€{q.varUnbilled.toFixed(2)}</div>
                              <div className="text-muted small" style={{ fontSize: '11px' }} title="Costo variabile dei partecipanti già fatturati">
                                €{q.varBilled.toFixed(2)} fatturati
                              </div>
                            </td>
                            <td className="text-end text-danger py-3" title="Somma dei costi extra del trimestre (tabella ExtraCosts): spese non legate ai partecipanti, es. costi Brevo rilevati dalle email in arrivo. Dettaglio nella riga espansa.">
                              {q.extraPriceTotal > 0 ? (
                                <>
                                  <div className="fw-bold">€{q.extraPriceTotal.toFixed(2)}</div>
                                  <div className="text-muted small" style={{ fontSize: '11px' }}>{q.extracosts.length} costi extra</div>
                                </>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                            <td className="text-end fw-bold pe-4 py-3" title="Totale = canone fisso €144 (in un solo gruppo) + quota variabile del gruppo + costi extra (da fatturare)">
                              <div className="text-nowrap">€{q.totUnbilled.toFixed(2)} da fatturare</div>
                              <div className="text-muted small fw-normal text-nowrap" style={{ fontSize: '11px' }}>€{q.totBilled.toFixed(2)} fatturati</div>
                            </td>
                            <td className="text-end pe-4 py-3">
                              <button
                                className="btn btn-sm btn-outline-secondary rounded-pill px-3 shadow-sm"
                                onClick={(e) => { e.stopPropagation(); exportQuarterJson(q); }}
                                title="Esporta fattura JSON"
                              >
                                <i className="bi bi-download me-1"></i>Esporta
                              </button>
                            </td>
                          </tr>

                          {/* Expanded Event Detail Rows */}
                          {isExpanded && q.events.map(ev => {
                            const st = statusLabels[ev.status || 0] || { label: 'Sconosciuto', color: 'secondary' };
                            return (
                              <tr key={ev.id} className="table-light">
                                <td className="ps-4" style={{ width: '40px' }}></td>
                                <td colSpan={9} className="py-2 pe-4">
                                  <div className="d-flex align-items-center justify-content-between ps-3 border-start border-primary border-3">
                                    <div className="d-flex align-items-center gap-3 flex-wrap">
                                      <div>
                                        <span className="fw-bold text-dark">{ev.name}</span>
                                        <span className="text-muted small ms-2">ID: #{ev.id}</span>
                                      </div>
                                      <span className="text-muted small">
                                        <i className="bi bi-calendar3 me-1"></i>
                                        {new Date(ev.date).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}
                                      </span>
                                      <span className="text-muted small">
                                        <i className="bi bi-geo-alt me-1"></i>
                                        {ev.city ? <span className="text-primary me-1">{ev.city.name} </span> : ''}
                                        {ev.location}
                                      </span>
                                      <span className={`badge rounded-pill bg-${st.color.split(' ')[0]} bg-opacity-10 text-${st.color.split(' ')[0]} fw-bold`}>
                                        {st.label}
                                      </span>
                                    </div>
                                    <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                      <span className="badge rounded-pill bg-primary fw-bold px-3 py-2" title="Partecipanti totali">
                                        <i className="bi bi-people me-1"></i>{ev.participantCount}
                                      </span>
                                      <span className="badge rounded-pill bg-warning bg-opacity-10 text-warning fw-bold px-3 py-2" title="Partecipanti da fatturare (Billed=0)">
                                        <i className="bi bi-x-circle me-1"></i>{ev.unbilledCount} da fatturare
                                      </span>
                                      <span className="badge rounded-pill bg-success bg-opacity-10 text-success fw-bold px-3 py-2" title="Partecipanti già fatturati (Billed=1)">
                                        <i className="bi bi-check-circle me-1"></i>{ev.billedCount} fatturati
                                      </span>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}

                          {/* SOLA-51: dettaglio costi extra del trimestre (tabella ExtraCosts) */}
                          {isExpanded && q.extracosts.length > 0 && (
                            <tr className="table-light">
                              <td className="ps-4" style={{ width: '40px' }}></td>
                              <td colSpan={9} className="py-2 pe-4">
                                <div className="ps-3 border-start border-danger border-3">
                                  <div className="fw-bold text-danger mb-2">
                                    <i className="bi bi-receipt me-1"></i>Costi extra del trimestre (tabella ExtraCosts)
                                  </div>
                                  <table className="table table-sm table-borderless mb-0" style={{ maxWidth: '720px' }}>
                                    <thead>
                                      <tr className="text-muted small text-uppercase">
                                        <th className="border-0">Data</th>
                                        <th className="border-0">Descrizione</th>
                                        <th className="border-0 text-end">Costo Extra</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {q.extracosts.map((ec, i) => (
                                        <tr key={i}>
                                          <td className="text-muted small text-nowrap">
                                            {new Date(ec.data).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}
                                          </td>
                                          <td>{ec.descrizione || <span className="text-muted">—</span>}</td>
                                          <td className="text-end fw-bold">€{ec.prezzo.toFixed(2)}</td>
                                        </tr>
                                      ))}
                                      <tr className="fw-bold">
                                        <td></td>
                                        <td className="text-end">Totale</td>
                                        <td className="text-end text-danger">€{q.extraPriceTotal.toFixed(2)}</td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}

                    {quarters.length === 0 && (
                      <tr>
                        <td colSpan={10} className="text-center py-5">
                          <div className="text-muted">
                            <i className="bi bi-calendar-x fa-3x mb-3 opacity-50 d-block"></i>
                            <p className="mb-0">Nessun evento completato (stato &gt; 5) né costi extra</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Pricing Info */}
        <div className="col-12">
          <div className="card border-0 shadow-sm glass">
            <div className="card-body">
              <h6 className="fw-bold mb-3">
                <i className="bi bi-info-circle"></i> Struttura dei Prezzi
              </h6>
              <div className="row g-3">
                <div className="col-md-4">
                  <div className="p-3 bg-light rounded">
                    <strong className="text-info">Costo Fisso Mensile</strong>
                    <p className="mb-0 mt-2 small">€48 (€36 hosting + €12 backup)</p>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="p-3 bg-light rounded">
                    <strong className="text-warning">Primo Tier (0-500 partecipanti)</strong>
                    <p className="mb-0 mt-2 small">€0.50 per partecipante</p>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="p-3 bg-light rounded">
                    <strong className="text-danger">Secondo Tier (500+ partecipante)</strong>
                    <p className="mb-0 mt-2 small">€1.00 per partecipante</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}